import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Archive, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { listRecipeTemplates, createRecipeFromTemplate } from "@/lib/api";

const TYPE_COLORS = {
  fill: "bg-sky-100 text-sky-700",
  click: "bg-violet-100 text-violet-700",
  wait: "bg-slate-100 text-slate-600",
  extract: "bg-emerald-100 text-emerald-700",
  navigate: "bg-amber-100 text-amber-700",
};

function stepValue(s) {
  if (s.type === "wait") return `${s.ms} ms`;
  if (s.is_date) return `data ${s.date_role === "end" ? "fim" : "início"} (automática, ${s.date_format})`;
  if (s.value === "${CRM_PASSWORD}") return "•••••• (palavra-passe)";
  if (s.value === "${CRM_EMAIL}") return "(email)";
  if (s.header_contains) return `tabela com “${s.header_contains}”`;
  return s.selector || s.value || "";
}

export default function TemplateRecipeDialog({ open, onOpenChange, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listRecipeTemplates()
      .then((t) => { setTemplates(t); setName(t[0]?.name || ""); })
      .catch(() => toast.error("Não foi possível carregar o modelo"))
      .finally(() => setLoading(false));
  }, [open]);

  const tpl = templates[0];

  async function create() {
    if (!tpl) return;
    setSaving(true);
    try {
      const rec = await createRecipeFromTemplate(tpl.id, { email, password, name });
      toast.success(`Rotina “${rec.name}” criada a partir do modelo`);
      setPassword("");
      onOpenChange(false);
      onCreated && onCreated(rec);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar rotina");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-primary" /> Modelo padrão</DialogTitle>
          <DialogDescription>Passos arquivados para recriar a rotina sempre que precisar.</DialogDescription>
        </DialogHeader>

        {loading || !tpl ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="font-medium" data-testid="template-name">{tpl.name}</div>
              <p className="text-xs text-muted-foreground mt-1">{tpl.description}</p>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <Badge variant="outline">{tpl.steps.length} passos</Badge>
                {tpl.schedule?.enabled && <Badge variant="outline">Agendada: dias úteis {tpl.schedule.start_time}–{tpl.schedule.end_time}, a cada {tpl.schedule.interval_minutes} min</Badge>}
                {tpl.feed_cutting_simulator && <Badge variant="outline">Alimenta Máquina de Corte</Badge>}
              </div>
              {tpl.recipes?.length > 0 && (
                <div className="mt-3 text-xs text-emerald-700 flex items-center gap-1" data-testid="template-existing-recipes">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Já existe: {tpl.recipes.map((r) => r.name).join(", ")}
                </div>
              )}
            </div>

            <ol className="space-y-1.5" data-testid="template-steps">
              {tpl.steps.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-right text-xs text-muted-foreground tabular-nums">{i + 1}</span>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium uppercase ${TYPE_COLORS[s.type] || "bg-muted"}`}>{s.type}</span>
                  <span className="font-medium">{s.label}</span>
                  <span className="text-xs text-muted-foreground truncate">{stepValue(s)}</span>
                </li>
              ))}
            </ol>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="tpl-name">Nome da rotina</Label>
                <Input id="tpl-name" data-testid="template-recipe-name-input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              {tpl.needs?.email && (
                <div className="space-y-1.5">
                  <Label htmlFor="tpl-email">Email do CRM</Label>
                  <Input id="tpl-email" data-testid="template-email-input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              )}
              {tpl.needs?.password && (
                <div className="space-y-1.5">
                  <Label htmlFor="tpl-pass">Palavra-passe (NIF)</Label>
                  <Input id="tpl-pass" data-testid="template-password-input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">O email e a palavra-passe ficam escritos nos passos da nova rotina.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="template-create-button" onClick={create} disabled={saving || !tpl || (tpl.needs?.email && !email) || (tpl.needs?.password && !password)}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Criar rotina a partir do modelo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
